export type LlmProvider = 'openai' | 'anthropic' | 'gemini';

export type WidgetPosition = 'bottom-right' | 'bottom-left';

export type BotLocale = 'en' | 'uk';

export interface LocalizedBotContent {
  name: string;
  welcomeMessage: string;
  suggestedPrompts: string[];
}

export interface BotConfig {
  id: string;
  ownerId: string;
  defaultLocale: BotLocale;
  content: Partial<Record<BotLocale, LocalizedBotContent>>;
  systemPrompt: string;
  llmProvider: LlmProvider;
  llmModel: string;
  /** AES-GCM encrypted with `v1$<iv>$<ct>` prefix. Never sent to the client. */
  encryptedApiKey?: string;
  primaryColor: string;
  logoUrl?: string;
  position: WidgetPosition;
  collectLeads: boolean;
  leadWebhookUrl?: string;
  handoffTelegramChatId?: string;
  /** Admin-controlled. When false, RAG is invisible to user and skipped in chat. */
  ragEnabled: boolean;
  /** Admin-controlled per-bot override of the global default (5000). */
  maxChunks?: number;
  /**
   * Phase 3f.2 — admin-controlled monthly USD hard cap. Universal: works
   * for both platform and bot-level keys. Hit either daily or monthly →
   * bot auto-disables until the window rolls over + owner gets an email.
   *
   * Renamed from `monthlyCostCapUsd` (Phase 3b's soft-cap field) so the
   * pair `dailyCostCap` + `monthlyCostCap` reads consistently. `getBotConfig`
   * backfills legacy KV blobs on read so we don't need a one-shot
   * migration script.
   */
  monthlyCostCap?: number;
  /**
   * Phase 3d.3 — admin-controlled auto-refresh cadence for sitemap + url
   * sources. Cron Worker re-ingests eligible sources every N hours.
   * undefined / null = off (default — file + text sources never refresh).
   * Valid values: 24 (daily), 168 (weekly), 720 (monthly).
   */
  refreshIntervalHours?: 24 | 168 | 720;
  /** Phase 3d.3 — server-managed timestamp of the last cron refresh pass. */
  lastRefreshAt?: string;
  /**
   * Phase 3f.1 — L1: max characters per single user message. Default 500.
   * Admin range 100–4000. Server-side zod is authoritative; widget reads
   * this to drive the live counter + maxlength attribute.
   */
  maxMessageChars?: number;
  /**
   * Phase 3f.3 — L3: max messages per visitor per day. Default 100.
   * Admin range 20–1000. Visitor scope is the localStorage-backed
   * `visitorId`, so the quota rolls per browser per day.
   */
  maxDailyMessagesPerVisitor?: number;
  /**
   * Phase 3f.2 — L4: hard daily USD cost cap. Hit either daily OR monthly
   * → bot auto-disabled until next UTC window rollover. Undefined / null
   * / 0 = no daily cap.
   */
  dailyCostCap?: number;
  /**
   * Phase 3f.2 — server-managed disable state (auto-set on cap hit OR
   * manually by admin). unix seconds (UTC). Undefined = bot live.
   */
  disabledUntil?: number;
  disabledReason?: 'daily_cost_cap' | 'monthly_cost_cap' | 'manual';
  /**
   * Phase 3f.3 — L5: Cloudflare Turnstile gate on first message of every
   * new conversation. Opt-in per bot — small low-traffic bots don't need
   * the friction. Site key is admin-pasted (from CF dashboard); secret
   * key is platform-wide env var.
   */
  turnstileEnabled?: boolean;
  turnstileSiteKey?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * What the widget fetches publicly. Must NOT contain: systemPrompt, any
 * webhook URL, the bot owner, LLM provider/model, or anything the site
 * operator would leak by embedding the widget in a hostile page.
 */
export type PublicBotConfig = Pick<
  BotConfig,
  | 'id'
  | 'defaultLocale'
  | 'content'
  | 'primaryColor'
  | 'logoUrl'
  | 'position'
  // Phase 3f.1 — widget needs the per-bot char limit to set <textarea>
  // maxlength + drive the live counter. Safe to expose: it's a UX hint,
  // server-side zod is authoritative on the real cap.
  | 'maxMessageChars'
  // Phase 3f.3 — widget needs both Turnstile fields to decide whether
  // to load the CF script + which sitekey to render. Sitekey is public
  // by design (CF dashboards expose it as the embed-side identifier);
  // the *secret* key lives only in the worker env.
  | 'turnstileEnabled'
  | 'turnstileSiteKey'
>;

/**
 * Fields the admin UI sends on create. Server generates id/ownerId/timestamps
 * and handles apiKey via a separate parameter (plaintext in transit only,
 * encrypted before KV write).
 */
export interface CreateBotInput {
  defaultLocale: BotLocale;
  content: Partial<Record<BotLocale, LocalizedBotContent>>;
  systemPrompt: string;
  llmProvider: LlmProvider;
  llmModel: string;
  primaryColor: string;
  logoUrl?: string;
  position: WidgetPosition;
  collectLeads: boolean;
  leadWebhookUrl?: string;
  handoffTelegramChatId?: string;
  /** Admin-only. Backend strips from non-admin patches and forces false on user-created bots. */
  ragEnabled?: boolean;
  /** Admin-only. Per-bot override of the platform default chunk quota. */
  maxChunks?: number;
  /** Admin-only. Phase 3f.2 — universal monthly USD hard cap. */
  monthlyCostCap?: number;
  /** Admin-only. Phase 3d.3 — auto-refresh cadence for sitemap + url sources. */
  refreshIntervalHours?: 24 | 168 | 720 | null;
  /** Phase 3f.1 — L1 per-message char limit. Range 100–4000. */
  maxMessageChars?: number;
  /** Phase 3f.3 — L3 visitor daily message quota. Range 20–1000. */
  maxDailyMessagesPerVisitor?: number;
  /** Phase 3f.2 — L4 daily USD cap. 0/null clears it. */
  dailyCostCap?: number | null;
  /** Phase 3f.3 — L5 Turnstile opt-in. */
  turnstileEnabled?: boolean;
  /** Phase 3f.3 — L5 Turnstile site key (from CF dashboard). */
  turnstileSiteKey?: string | null;
}

/** Partial update. apiKey travels as a separate param, not here. */
export type UpdateBotInput = Partial<CreateBotInput>;

/** Dedicated shape for rotate-key endpoint. */
export interface BotApiKeyInput {
  apiKey: string;
}

/** List-view DTO. Excludes sensitive fields. */
export interface AdminBotSummary {
  id: string;
  name: string;
  defaultLocale: BotLocale;
  isActive: boolean;
  hasApiKey: boolean;
  primaryColor: string;
  /** Owner UUID — always present so admins can scope mutations. */
  ownerId: string;
  /** Owner email — populated only when caller has admin role; null for owners viewing their own list. */
  ownerEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Detail-view DTO. All BotConfig fields except `encryptedApiKey`, plus boolean. */
export type AdminBotDetail = Omit<BotConfig, 'encryptedApiKey'> & {
  hasApiKey: boolean;
};

/**
 * Resolve which locale's bot content to render.
 *
 * Fallback chain:
 *   1. requested locale (if present in content)
 *   2. config.defaultLocale
 *   3. first populated locale in BotLocale order
 *   4. null — bot has no content at all (misconfiguration)
 *
 * Returns the locale actually used so the widget can sync UI chrome to
 * match (see Phase 1d: "locale consistency on fallback").
 */
export function resolveBotContent(
  config: Pick<BotConfig, 'content' | 'defaultLocale'>,
  requested: BotLocale,
): { content: LocalizedBotContent; locale: BotLocale } | null {
  const chain: BotLocale[] = [requested, config.defaultLocale, 'en', 'uk'];
  const seen = new Set<BotLocale>();
  for (const locale of chain) {
    if (seen.has(locale)) continue;
    seen.add(locale);
    const content = config.content[locale];
    if (content) return { content, locale };
  }
  return null;
}
